import React from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Container,
  Col,
  Row,
} from "reactstrap";
import { ToastContainer } from "react-toastify";
import { Link } from "react-router-dom";
import SimpleHeader from "@/components/Headers/SimpleHeader";
import TableWrapper from "@/utils/TableWrapper";

type CategoryIdHandler = (categoryId: string) => void;

type SiteCategoriesViewProps = {
  permission?: { edit?: boolean; publish?: boolean } | null;
  categoriesToAdd: unknown[];
  reformattedCategories?: unknown[];
  onAdd: CategoryIdHandler;
  onDelete: CategoryIdHandler;
  onLabel: CategoryIdHandler;
  onScore: CategoryIdHandler;
  isAdmin?: boolean;
};

const SiteCategoriesView = ({
  permission,
  categoriesToAdd,
  reformattedCategories,
  onAdd,
  onDelete,
  onLabel,
  onScore,
  isAdmin,
}: SiteCategoriesViewProps) => {
  // Columns for "Assign New Category"
  const columnsToAdd = [
    { accessorKey: "name", header: "Name" },
    { accessorKey: "slug", header: "Slug" },
    {
      id: "link",
      header: "ACTION",
      cell: ({ row }) => (
        <div>
          <Button
            className="btn-round btn-icon"
            color="primary"
            onClick={(e) => {
              e.preventDefault();
              onAdd(row.original._id);
            }}
            size="sm"
          >
            <span className="btn-inner--icon mr-1">
              <i className="ni ni-fat-add" />
            </span>
            <span className="btn-inner--text">Add</span>
          </Button>
        </div>
      ),
    },
  ];

  // Columns for "Site Categories"
  const linkedColumns = ([
    { accessorKey: "name", header: "Name" },
    { accessorKey: "slug", header: "Slug" },
    {
      accessorFn: (row) => row.label?.[0]?.value?.label,
      header: "Label",
    },
  ] as any[]);

  if (permission?.edit || permission?.publish) {
    linkedColumns.push({
      header: "ACTION",
      cell: ({ row }) => (
        <>
          <Button
            className="btn-round btn-icon"
            color="danger"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              onDelete(row.original._id);
            }}
          >
            <span className="btn-inner--icon mr-1">
              <i className="fas fa-trash" />
            </span>
            <span className="btn-inner--text">Remove</span>
          </Button>

          <Button
            className="btn-round btn-icon"
            color="success"
            size="sm"
            onClick={(e) => {
              e.preventDefault();
              onLabel(row.original._id);
            }}
          >
            <span className="btn-inner--icon mr-1">
              <i className="ni ni-fat-add" />
            </span>
            <span className="btn-inner--text">Label</span>
          </Button>

          {/* SCRUM-1850: the Scoring route exists for administrators only. */}
          {isAdmin && (
            <Button
              className="btn-round btn-icon"
              color="info"
              size="sm"
              onClick={(e) => {
                e.preventDefault();
                onScore(row.original._id);
              }}
            >
              <span className="btn-inner--text">Scoring</span>
            </Button>
          )}
        </>
      ),
    });
  }

  return (
    <>
      {/* NOTIFICATIONS */}
      <div className="rna-wrapper">
        <ToastContainer />
      </div>

      <SimpleHeader name="Site Categories" parentName="Categories" />
      <Container className="mt--6" fluid>
        <Card>
          <CardHeader>
            <Row className="align-items-center">
              <Col xs="8">
                <h5 className="h3 mb-0">Assign New Category</h5>
              </Col>
              {permission?.publish || permission?.edit ? (
                <Col className="text-right" xs="4">
                  <Button color="default" size="sm" to="/admin/category/add" tag={Link}>
                    Add New Category
                  </Button>
                </Col>
              ) : null}
            </Row>
          </CardHeader>

          <CardBody>
            <TableWrapper
              data={categoriesToAdd}
              columns={columnsToAdd}
              searchId="categoriesToAddSearchInput"
            />
          </CardBody>
        </Card>

        {(reformattedCategories?.length ?? 0) > 0 && (
          <Card>
            <CardHeader>
              <h3 className="mb-0">Site Categories</h3>
            </CardHeader>
            <CardBody>
              <TableWrapper
                data={reformattedCategories}
                columns={linkedColumns}
                searchId="siteCategoriesSearchInput"
              />
            </CardBody>
          </Card>
        )}
      </Container>
    </>
  );
};

export default React.memo(SiteCategoriesView);
